import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, ArrowLeft, Ticket, ExternalLink, Info, CheckCircle } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { getEventBySlug, events, isEventPast } from '../data/events';
export function EventDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const event = slug ? getEventBySlug(slug) : undefined;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!event) {
    return <Navigate to="/events" replace data-id="element-706" />;
  }
  const isPast = isEventPast(event.date);
  const relatedEvents = events.filter(e => e.id !== event.id && e.category === event.category && !isEventPast(e.date)).slice(0, 3);
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Music: 'from-purple-500 to-pink-500',
      'Arts & Culture': 'from-amber-500 to-orange-500',
      Sports: 'from-emerald-500 to-teal-500',
      'Food & Drink': 'from-rose-500 to-red-500',
      Business: 'from-blue-500 to-indigo-500',
      Family: 'from-green-500 to-lime-500'
    };
    return colors[category] || 'from-gray-500 to-gray-600';
  };
  return <div className="min-h-screen bg-gradient-to-b from-white to-gray-50" data-id="element-707">
      {/* Hero Image */}
      <div className="relative h-[70vh] overflow-hidden" data-id="element-708">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-709" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-id="element-710" />

        <Link to="/events" className="absolute top-8 left-8 bg-white/95 backdrop-blur-sm text-charcoal px-5 py-3 rounded-full hover:bg-white transition-colors flex items-center gap-2 text-sm font-semibold shadow-lg" data-id="element-711">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} data-id="element-712" />
          Back to events
        </Link>

        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-8 pb-12" data-id="element-713">
          <div className="max-w-7xl mx-auto" data-id="element-714">
            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.2
          }} data-id="element-715">
              <div className="flex flex-wrap gap-3 mb-6" data-id="element-716">
                <span className={`px-5 py-2.5 bg-gradient-to-r ${getCategoryColor(event.category)} text-white text-sm font-bold rounded-full shadow-lg`} data-id="element-717">
                  {event.category.toUpperCase()}
                </span>
                {isPast ? <span className="px-5 py-2.5 bg-gray-800/90 backdrop-blur-sm text-white text-sm font-bold rounded-full flex items-center gap-2" data-id="element-718">
                    <CheckCircle className="w-4 h-4" strokeWidth={2.5} data-id="element-719" />
                    EVENT COMPLETED
                  </span> : event.featured ? <span className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold rounded-full shadow-lg" data-id="element-720">
                    FEATURED
                  </span> : null}
              </div>
              <h1 className="font-display text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-id="element-721">
                {event.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-white text-lg" data-id="element-722">
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full" data-id="element-723">
                  <Calendar className="w-5 h-5" strokeWidth={2} data-id="element-724" />
                  <span className="font-semibold" data-id="element-725">
                    {new Date(event.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full" data-id="element-726">
                  <Clock className="w-5 h-5" strokeWidth={2} data-id="element-727" />
                  <span className="font-semibold" data-id="element-728">{event.time}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-id="element-729">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-730">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-731">
            {/* Past Event Notice */}
            {isPast && <motion.div className="bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 rounded-2xl p-8" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-732">
                <div className="flex items-start gap-4" data-id="element-733">
                  <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-734">
                    <CheckCircle className="w-6 h-6 text-white" strokeWidth={2.5} data-id="element-735" />
                  </div>
                  <div data-id="element-736">
                    <h3 className="font-display text-2xl font-bold text-charcoal mb-2" data-id="element-737">
                      This Event Has Concluded
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-738">
                      This event took place on{' '}
                      {new Date(event.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      . Check out our upcoming events for more exciting
                      experiences in Nairobi.
                    </p>
                    <Link to="/events" className="inline-block mt-4 px-6 py-3 bg-charcoal text-white rounded-full font-semibold hover:bg-gray-800 transition-colors" data-id="element-739">
                      View Upcoming Events
                    </Link>
                  </div>
                </div>
              </motion.div>}

            {/* Description */}
            <section data-id="element-740">
              <h2 className="font-display text-4xl font-bold text-charcoal mb-6" data-id="element-741">
                About this event
              </h2>
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{
              __html: event.fullDescription
            }} data-id="element-742" />
            </section>

            {/* Highlights */}
            <section data-id="element-743">
              <h2 className="font-display text-4xl font-bold text-charcoal mb-6" data-id="element-744">
                Event highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-745">
                {event.highlights.map((highlight, index) => <motion.div key={index} className="flex items-start gap-3 p-5 bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-100 rounded-xl" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.1 * index
              }} data-id="element-746">
                    <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0" data-id="element-747" />
                    <p className="text-gray-700 font-medium" data-id="element-748">{highlight}</p>
                  </motion.div>)}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-749">
            <div className="sticky top-24 space-y-6" data-id="element-750">
              {/* Tickets */}
              <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 shadow-elegant" data-id="element-751">
                <h3 className="font-display text-2xl font-bold text-charcoal mb-6" data-id="element-752">
                  {isPast ? 'Ticket Information' : 'Get Tickets'}
                </h3>

                <div className="space-y-4 mb-8" data-id="element-753">
                  {event.tickets.map((ticket, index) => <div key={index} className={`p-5 border-2 rounded-xl ${ticket.available && !isPast ? 'border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50' : 'border-gray-200 bg-gray-50 opacity-70'}`} data-id="element-754">
                      <div className="flex items-start justify-between mb-3" data-id="element-755">
                        <div data-id="element-756">
                          <h4 className="font-bold text-charcoal text-lg" data-id="element-757">
                            {ticket.type}
                          </h4>
                          <p className="font-display text-3xl font-bold text-teal-600 mt-1" data-id="element-758">
                            KES {ticket.price.toLocaleString()}
                          </p>
                        </div>
                        {(!ticket.available || isPast) && <span className="text-xs bg-gray-200 text-gray-700 px-3 py-1 rounded-full font-bold" data-id="element-759">
                            {isPast ? 'PAST' : 'SOLD OUT'}
                          </span>}
                      </div>
                      {ticket.benefits && <ul className="space-y-2 text-sm text-gray-700" data-id="element-760">
                          {ticket.benefits.map((benefit, i) => <li key={i} className="flex items-start gap-2" data-id="element-761">
                              <span className="text-teal-500 mt-0.5" data-id="element-762">✓</span>
                              <span data-id="element-763">{benefit}</span>
                            </li>)}
                        </ul>}
                    </div>)}
                </div>

                {!isPast && <>
                    <a href="https://www.ticketsasa.com" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-full hover:shadow-lg transition-smooth font-bold text-lg" data-id="element-764">
                      <Ticket className="w-6 h-6" strokeWidth={2.5} data-id="element-765" />
                      Buy Tickets Now
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-4" data-id="element-766">
                      Secure payment via TicketSasa
                    </p>
                  </>}
              </div>

              {/* Venue */}
              <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 shadow-elegant" data-id="element-767">
                <h3 className="font-display text-2xl font-bold text-charcoal mb-6" data-id="element-768">
                  Venue
                </h3>
                <div className="space-y-4" data-id="element-769">
                  <div data-id="element-770">
                    <h4 className="font-bold text-charcoal mb-2 text-lg" data-id="element-771">
                      {event.venue.name}
                    </h4>
                    <p className="text-sm text-gray-700 mb-3" data-id="element-772">
                      {event.venue.address}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg" data-id="element-773">
                      <Users className="w-4 h-4" strokeWidth={2} data-id="element-774" />
                      <span data-id="element-775">
                        Capacity: {event.venue.capacity.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <a href={event.venue.mapUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-semibold" data-id="element-776">
                    <MapPin className="w-4 h-4" strokeWidth={2} data-id="element-777" />
                    <span data-id="element-778">View on Google Maps</span>
                    <ExternalLink className="w-4 h-4 ml-auto" strokeWidth={2} data-id="element-779" />
                  </a>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-100 rounded-2xl p-8" data-id="element-780">
                <div className="flex items-center gap-3 mb-6" data-id="element-781">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center" data-id="element-782">
                    <Info className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-783" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-charcoal" data-id="element-784">
                    Important Info
                  </h3>
                </div>
                <div className="space-y-3 text-sm text-gray-700" data-id="element-785">
                  {event.ageRestriction && <div className="flex gap-2" data-id="element-786">
                      <span className="font-bold min-w-[100px]" data-id="element-787">Age:</span>
                      <span data-id="element-788">{event.ageRestriction}</span>
                    </div>}
                  {event.dresscode && <div className="flex gap-2" data-id="element-789">
                      <span className="font-bold min-w-[100px]" data-id="element-790">
                        Dress code:
                      </span>
                      <span data-id="element-791">{event.dresscode}</span>
                    </div>}
                  <div className="flex gap-2" data-id="element-792">
                    <span className="font-bold min-w-[100px]" data-id="element-793">Organizer:</span>
                    <span data-id="element-794">{event.organizer}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Events */}
        {relatedEvents.length > 0 && <div className="mt-20 pt-12 border-t-2 border-gray-200" data-id="element-795">
            <div className="flex items-center gap-3 mb-8" data-id="element-796">
              <div className="h-1 w-12 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full" data-id="element-797" />
              <h2 className="font-display text-4xl font-bold text-charcoal" data-id="element-798">
                More {event.category} events
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-799">
              {relatedEvents.map(relatedEvent => <Link key={relatedEvent.id} to={`/events/${relatedEvent.slug}`} className="group block" data-id="element-800">
                  <div className="border-2 border-gray-100 hover:border-teal-400 transition-smooth overflow-hidden rounded-2xl bg-white shadow-elegant hover:shadow-elegant-hover" data-id="element-801">
                    <div className="relative aspect-[4/3] overflow-hidden" data-id="element-802">
                      <img src={relatedEvent.image} alt={relatedEvent.title} className="w-full h-full object-cover group-hover:scale-105 transition-smooth" data-id="element-803" />
                      <div className="absolute top-4 left-4" data-id="element-804">
                        <span className={`px-4 py-2 bg-gradient-to-r ${getCategoryColor(relatedEvent.category)} text-white text-xs font-bold rounded-full shadow-lg`} data-id="element-805">
                          {relatedEvent.category.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="p-6" data-id="element-806">
                      <h3 className="font-display text-xl font-bold text-charcoal mb-3 group-hover:text-teal-600 transition-colors line-clamp-2" data-id="element-807">
                        {relatedEvent.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4" data-id="element-808">
                        <Calendar className="w-4 h-4" strokeWidth={2} data-id="element-809" />
                        <span data-id="element-810">
                          {new Date(relatedEvent.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-teal-600 group-hover:text-teal-700" data-id="element-811">
                        View event →
                      </span>
                    </div>
                  </div>
                </Link>)}
            </div>
          </div>}
      </div>

      <ScrollToTop data-id="element-812" />
      <Footer data-id="element-813" />
    </div>;
}